import type { ECharts } from "echarts/core";
import { type CSSProperties, type JSX, type ReactNode, useEffect, useState } from "react";
import { layoutTextMeasurer } from "@/utils/text";
import type { TimeEvent } from "../../band-data/types";
import {
    CHART_FONT_FAMILY,
    SPAN_PILL_BACKGROUND,
    SPAN_PILL_COLOR,
    SPAN_PILL_FONT,
    SPAN_PILL_FONT_SIZE_PX,
    SPAN_PILL_FONT_WEIGHT,
    SPAN_PILL_HEIGHT_PX,
    SPAN_PILL_LINE_HEIGHT_PX,
    SPAN_PILL_MARGIN_PX,
    SPAN_PILL_PADDING_BOTTOM_PX,
    SPAN_PILL_PADDING_TOP_PX,
    SPAN_PILL_PADDING_X_PX,
    SPAN_PILL_RADIUS_PX,
    SPAN_PILL_SHADOW,
} from "../layout/plot-geometry";
import type { PlotLayout } from "../layout/plot-layout";
import { BAND_ABOVE_RESERVE_PX, SPAN_PILL_ROW_PX } from "./band-geometry";
import { formatSpan } from "./format-span";

const LABEL_OPACITY = 0.6;
const SEPARATOR = " · ";

const PILL_STYLE: CSSProperties = {
    height: SPAN_PILL_HEIGHT_PX,
    paddingTop: SPAN_PILL_PADDING_TOP_PX,
    paddingBottom: SPAN_PILL_PADDING_BOTTOM_PX,
    paddingInline: SPAN_PILL_PADDING_X_PX,
    borderRadius: SPAN_PILL_RADIUS_PX,
    backgroundColor: SPAN_PILL_BACKGROUND,
    boxShadow: SPAN_PILL_SHADOW,
    color: SPAN_PILL_COLOR,
    fontFamily: CHART_FONT_FAMILY,
    fontSize: SPAN_PILL_FONT_SIZE_PX,
    fontWeight: SPAN_PILL_FONT_WEIGHT,
    lineHeight: `${SPAN_PILL_LINE_HEIGHT_PX}px`,
    // The page's tracking would make the text wider than the canvas measured it.
    letterSpacing: 0,
};

/** Near either end of the plot a centred pill would run off it, so it slides along the line to stay inside. */
function clampPillLeft(centerX: number, widthPx: number, minX: number, maxX: number): number {
    return Math.max(minX, Math.min(centerX - widthPx / 2, maxX - widthPx));
}

function pillWidth(text: string): number {
    return layoutTextMeasurer(text, SPAN_PILL_FONT) + 2 * SPAN_PILL_PADDING_X_PX;
}

type AxisPointerEvent = { axesInfo?: { axisDim: string; axisIndex: number; value: number }[] };

/** The plot pointer's time, snapped by ECharts to the nearest data point; null while hidden. */
function usePlotPointerMs(chart: ECharts | null): number | null {
    const [pointerMs, setPointerMs] = useState<number | null>(null);

    useEffect(() => {
        if (!chart) return undefined;
        const onUpdate = (event: unknown) => {
            const plotAxis = (event as AxisPointerEvent).axesInfo?.find(
                (info) => info.axisDim === "x" && info.axisIndex === 0,
            );
            setPointerMs(plotAxis ? Number(plotAxis.value) : null);
        };
        chart.on("updateAxisPointer", onUpdate);
        return () => {
            chart.off("updateAxisPointer", onUpdate);
            setPointerMs(null);
        };
    }, [chart]);

    return pointerMs;
}

function Pill({ left, top, width, children }: { left: number; top: number; width: number; children: ReactNode }) {
    return (
        <div className={"absolute text-center whitespace-nowrap"} style={{ ...PILL_STYLE, left, top, width }}>
            {children}
        </div>
    );
}

/** The scheduled and valve-open times of the span under the pointer, on the dashed line beside the band. */
export function BandSpanPill({
    chart,
    layout,
    panelWidthPx,
    position,
    hovered,
}: {
    chart: ECharts | null;
    layout: PlotLayout;
    panelWidthPx: number;
    position: "above" | "below";
    hovered: { scheduled?: TimeEvent; actual?: TimeEvent };
}): JSX.Element | null {
    const pointerMs = usePlotPointerMs(chart);

    if (pointerMs === null || !chart) return null;

    const x = chart.convertToPixel({ xAxisIndex: 0 }, pointerMs);
    if (typeof x !== "number" || !Number.isFinite(x)) return null;

    const nowMs = Date.now();
    const parts = [
        { key: "scheduled", label: "Scheduled", span: hovered.scheduled },
        { key: "actual", label: "Valve open", span: hovered.actual },
    ].flatMap(({ key, label, span }) => (span ? [{ key, label, range: formatSpan(span, nowMs) }] : []));
    if (parts.length === 0) return null;

    const width = pillWidth(parts.map(({ label, range }) => `${label} ${range}`).join(SEPARATOR));
    const pill = (
        <Pill
            left={clampPillLeft(x, width, layout.gutters.left, panelWidthPx - layout.gutters.right)}
            top={position === "below" ? SPAN_PILL_MARGIN_PX : 0}
            width={width}
        >
            {parts.map(({ key, label, range }, index) => (
                <span key={key}>
                    <span style={{ opacity: LABEL_OPACITY }}>
                        {index > 0 && SEPARATOR}
                        {label}{" "}
                    </span>
                    {range}
                </span>
            ))}
        </Pill>
    );

    if (position === "above") {
        return (
            <div
                className={"pointer-events-none absolute inset-x-0"}
                style={{ top: BAND_ABOVE_RESERVE_PX - SPAN_PILL_ROW_PX, height: SPAN_PILL_ROW_PX }}
            >
                {pill}
            </div>
        );
    }
    return (
        <div className={"pointer-events-none absolute inset-x-0 bottom-0"} style={{ height: layout.xAxisBandPx }}>
            {pill}
        </div>
    );
}
